import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DealItemGenericProperty } from '@/lib/graphql/generated/graphql';
import { Loader2, Plus, Trash2 } from 'lucide-react';

const HIDDEN_OPTION_NAME = '_index';

let optionRowIdCounter = 0;
const nextOptionRowId = () => `option-row-${optionRowIdCounter++}`;

export interface EditVariantPayload {
  name: string;
  applyToAllVariants: boolean;
  options: DealItemGenericProperty[];
}

interface EditVariantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  initialOptions: DealItemGenericProperty[];
  hasVariantSiblings: boolean;
  siblingOptionNames: string[];
  onSave: (payload: EditVariantPayload) => Promise<boolean> | boolean | void;
}

interface OptionRow {
  id: string;
  name: string;
  value: string;
}

export const EditVariantDialog: React.FC<EditVariantDialogProps> = ({
  open,
  onOpenChange,
  initialName,
  initialOptions,
  hasVariantSiblings,
  siblingOptionNames,
  onSave,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [applyToAllVariants, setApplyToAllVariants] = useState(false);
  const [rows, setRows] = useState<OptionRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setApplyToAllVariants(false);
    const ownRows = initialOptions
      .filter((option) => option.name !== HIDDEN_OPTION_NAME)
      .map((option) => ({
        id: nextOptionRowId(),
        name: option.name,
        value: option.value,
      }));
    const ownNames = new Set(ownRows.map((row) => row.name));
    const inheritedRows = siblingOptionNames
      .filter((name) => name !== HIDDEN_OPTION_NAME && !ownNames.has(name))
      .map((name) => ({ id: nextOptionRowId(), name, value: '' }));
    setRows([...ownRows, ...inheritedRows]);
  }, [open, initialName, initialOptions, siblingOptionNames]);

  const updateRow = (index: number, patch: Partial<OptionRow>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: nextOptionRowId(), name: '', value: '' },
    ]);
  };

  const handleSave = async () => {
    if (isSaving) return;
    const hiddenOptions = initialOptions.filter(
      (option) => option.name === HIDDEN_OPTION_NAME
    );
    const editedOptions: DealItemGenericProperty[] = rows
      .map((row) => ({
        name: row.name.trim(),
        value: row.value.trim(),
      }))
      .filter((option) => option.name !== '' && option.value !== '');

    setIsSaving(true);
    try {
      const result = await onSave({
        name: name.trim(),
        applyToAllVariants: hasVariantSiblings && applyToAllVariants,
        options: [
          ...editedOptions,
          ...hiddenOptions.map(({ name, value }) => ({ name, value })),
        ],
      });
      if (result === false) return;
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isSaving) return;
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('components.edit_variant_dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('components.edit_variant_dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-variant-name">
              {t('components.edit_variant_dialog.name_label')}
            </Label>
            <Input
              id="edit-variant-name"
              className="h-10"
              value={name}
              onChange={(event) => setName(event.target.value)}
              data-testid="edit-variant-name"
            />
            {hasVariantSiblings && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-variant-apply-all"
                  checked={applyToAllVariants}
                  onCheckedChange={(checked) =>
                    setApplyToAllVariants(checked === true)
                  }
                  data-testid="edit-variant-apply-all"
                />
                <Label
                  htmlFor="edit-variant-apply-all"
                  className="text-sm font-normal text-gray-500"
                >
                  {t('components.edit_variant_dialog.apply_to_all_variants')}
                </Label>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>
              {t('components.edit_variant_dialog.options_section_title')}
            </Label>
            {rows.length === 0 ? (
              <p className="text-sm text-gray-400">
                {t('components.edit_variant_dialog.empty_state')}
              </p>
            ) : (
              <div className="space-y-2">
                {rows.map((row, index) => (
                  <div key={row.id} className="flex items-end gap-2">
                    <div className="flex-1 space-y-1">
                      <Label
                        htmlFor={`edit-variant-option-name-${row.id}`}
                        className="text-sm font-medium"
                      >
                        {t('components.edit_variant_dialog.option_name_label')}
                      </Label>
                      <Input
                        id={`edit-variant-option-name-${row.id}`}
                        className="h-10"
                        value={row.name}
                        onChange={(event) =>
                          updateRow(index, { name: event.target.value })
                        }
                        placeholder={t(
                          'components.edit_variant_dialog.option_name_placeholder'
                        )}
                        data-testid={`edit-variant-option-name-${row.id}`}
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label
                        htmlFor={`edit-variant-option-value-${row.id}`}
                        className="text-sm font-medium"
                      >
                        {t('components.edit_variant_dialog.option_value_label')}
                      </Label>
                      <Input
                        id={`edit-variant-option-value-${row.id}`}
                        className="h-10"
                        value={row.value}
                        onChange={(event) =>
                          updateRow(index, { value: event.target.value })
                        }
                        placeholder={t(
                          'components.edit_variant_dialog.option_value_placeholder'
                        )}
                        data-testid={`edit-variant-option-value-${row.id}`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(index)}
                      aria-label={t('general.delete')}
                      data-testid={`edit-variant-option-remove-${row.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              data-testid="edit-variant-add-option"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('components.edit_variant_dialog.add_option')}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSaving}
            onClick={() => onOpenChange(false)}
          >
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving || name.trim() === ''}
            data-testid="edit-variant-save"
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('general.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
